<template>
  <template v-if="hasText">
    <div
      ref="reference"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @mousemove="handleMouseMove"
      @focusin="handleFocusIn"
      @focusout="handleFocusOut"
      :tabindex="focusable ? 0 : -1"
      :aria-label="ariaLabel && `Tooltip: ${ariaLabel}`"
      :aria-describedby="tooltipId"
      class="inline-block focus:outline-none"
      :class="class"
    >
      <slot />
      <span class="sr-only"> Tooltip contents: {{ text }} </span>
    </div>

    <teleport to="body">
      <transition name="fade">
        <div
          v-if="open"
          key="tooltip"
          ref="floating"
          :id="tooltipId"
          role="tooltip"
          :style="followCursor && !openedByFocus ? cursorStyles : floatingStyles"
          class="pointer-events-none absolute z-50 max-w-xs whitespace-pre-line rounded-lg border border-secondary-foreground bg-background px-1 py-1 text-center text-xs text-foreground shadow-md transition-opacity duration-150"
        >
          {{ text }}
        </div>
      </transition>
    </teleport>
  </template>

  <!-- This avoids weird accessibility issues with tabindex -->
  <template v-else>
    <slot />
  </template>
</template>

<script setup>
import { ref, watchEffect, computed } from "vue";
import { useFloating, offset, shift, flip, autoUpdate } from "@floating-ui/vue";

const props = defineProps({
  ariaLabel: { type: String, required: false },
  text: { type: String, default: "" },
  class: { type: [String, Array, Object], default: "" },
  followCursor: { type: Boolean, default: false },
  delay: { type: Number, default: 200 },
  side: {
    type: String,
    default: "top",
    validator: (value) => ["top", "bottom", "left", "right"].includes(value),
  },
  focusable: {
    type: Boolean,
    default: false,
  },
  childFocusShowsTooltip: {
    type: Boolean,
    default: false,
  },
});

const open = ref(false);
const reference = ref(null);
const floating = ref(null);
const mousePosition = ref({ x: 0, y: 0 });
const hoverTimeout = ref(null);
const tooltipId = `tooltip-${Math.random().toString(36).slice(2, 8)}`;
const openedByFocus = ref(false); // track if focus opened the tooltip

const hasText = computed(() => props.text?.trim()?.length > 0);

const { floatingStyles, update } = useFloating(reference, floating, {
  placement: props.side,
  middleware: [offset(8), shift(), flip()],
});

const cursorStyles = computed(() => ({
  position: "fixed",
  left: `${mousePosition.value.x + 10}px`,
  top: `${mousePosition.value.y - 10}px`,
  transform: "none",
}));

const handleMouseEnter = () => {
  if (!hasText.value) return;
  openedByFocus.value = false;
  if (props.delay > 0) {
    hoverTimeout.value = setTimeout(() => {
      open.value = true;
    }, props.delay);
  } else {
    open.value = true;
  }
};

const handleMouseLeave = () => {
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value);
    hoverTimeout.value = null;
  }
  open.value = false;
};

const handleMouseMove = (event) => {
  if (props.followCursor) {
    openedByFocus.value = false;
    mousePosition.value = { x: event.clientX, y: event.clientY };
  }
};

const handleFocusIn = () => {
  if (!hasText.value) return;
  if (!props.focusable && !props.childFocusShowsTooltip) return;
  open.value = true;
  openedByFocus.value = true;
  if (reference.value && floating.value) {
    update();
  }
};

const handleFocusOut = () => {
  open.value = false;
  openedByFocus.value = false;
};

const handleKeydown = (e) => {
  if (e.key === "Escape") {
    open.value = false;
    openedByFocus.value = false;
  }
};

watchEffect((onCleanup) => {
  if (open.value) {
    window.addEventListener("keydown", handleKeydown);
    onCleanup(() => window.removeEventListener("keydown", handleKeydown));
  }
});

// autoUpdate only if not following cursor OR opened by focus
watchEffect((onCleanup) => {
  if (
    reference.value &&
    floating.value &&
    open.value &&
    hasText.value &&
    (!props.followCursor || openedByFocus.value)
  ) {
    const cleanup = autoUpdate(reference.value, floating.value, update);
    onCleanup(cleanup);
  }
});
</script>
